/**
 * ShareableLinkPage
 *
 * Ported from: mobile shareable_link.dart
 *
 * Features:
 *   1. Activate / deactivate the shareable link (toggle → PATCH API instantly)
 *   2. Display + copy the user's public profile URL when active
 *   3. Per-platform visibility toggles (PATCH with visible_social_links on each change)
 *   4. Tip linking back to Edit Profile to add social links
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from '@tanstack/react-router';
import {
  FiArrowLeft,
  FiLink,
  FiCopy,
  FiCheck,
  FiExternalLink,
  FiEye,
  FiEyeOff,
} from 'react-icons/fi';
import { Header } from '../../layout/Header';
import { BottomNav } from '../../layout/BottomNav';
import { useAuth } from '../../../hooks/useAuth';
import { useGetShareableLink, useUpdateShareableLink } from '../../../api/settings';
import { OptionalBadge } from '../../ui/FormLabel';

// ── Platform metadata ─────────────────────────────────────────────────────────

interface PlatformMeta {
  label: string;
  emoji: string;
  /** Key on the User object (camelCase) used to check if the user has it set */
  userKey: string;
}

const PLATFORMS: Array<{ apiKey: string } & PlatformMeta> = [
  { apiKey: 'spotify', label: 'Spotify', emoji: '🎵', userKey: 'spotify' },
  { apiKey: 'apple_music', label: 'Apple Music', emoji: '🎵', userKey: 'appleMusic' },
  { apiKey: 'amazon_music', label: 'Amazon Music', emoji: '🛒', userKey: 'amazonMusic' },
  { apiKey: 'youtube_music', label: 'YouTube Music', emoji: '▶️', userKey: 'youtubeMusic' },
  { apiKey: 'soundcloud', label: 'SoundCloud', emoji: '☁️', userKey: 'soundcloud' },
  { apiKey: 'instagram', label: 'Instagram', emoji: '📸', userKey: 'instagram' },
  { apiKey: 'twitter', label: 'Twitter (X)', emoji: '𝕏', userKey: 'twitter' },
  { apiKey: 'tiktok', label: 'TikTok', emoji: '🎬', userKey: 'tiktok' },
  { apiKey: 'website', label: 'Website', emoji: '🌐', userKey: 'website' },
  { apiKey: 'facebook', label: 'Facebook', emoji: '👤', userKey: 'facebook' },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

const Spinner = ({ size = 16 }: { size?: number }) => (
  <span
    style={{ width: size, height: size }}
    className="inline-block border-2 border-white/20 border-t-white rounded-full animate-spin"
  />
);

/** Tailwind toggle switch component */
function Toggle({
  checked,
  onChange,
  loading = false,
  size = 'md',
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  loading?: boolean;
  size?: 'sm' | 'md';
}) {
  const isSmall = size === 'sm';
  const trackW = isSmall ? 'w-9' : 'w-11';
  const trackH = isSmall ? 'h-5' : 'h-6';
  const thumbW = isSmall ? 'w-3.5 h-3.5' : 'w-4 h-4';
  const thumbTranslate = isSmall
    ? checked
      ? 'translate-x-[18px]'
      : 'translate-x-0.5'
    : checked
      ? 'translate-x-6'
      : 'translate-x-1';

  if (loading) {
    return (
      <div className={`${trackW} flex justify-center`}>
        <Spinner size={isSmall ? 14 : 18} />
      </div>
    );
  }

  return (
    <button
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex items-center rounded-full transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500/50 ${trackW} ${trackH} ${checked ? 'bg-cyan-500' : 'bg-zinc-700'}`}
    >
      <span
        className={`inline-block rounded-full bg-white shadow transition-transform duration-200 ${thumbW} ${thumbTranslate}`}
      />
    </button>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export const ShareableLinkPage: React.FC = () => {
  const { user } = useAuth();
  const { data: linkData, isLoading } = useGetShareableLink();
  const updateMut = useUpdateShareableLink();

  const [isActive, setIsActive] = useState(false);
  const [visibleLinks, setVisibleLinks] = useState<Record<string, boolean>>({});
  const [copied, setCopied] = useState(false);
  const [togglingMain, setTogglingMain] = useState(false);
  const [savingPlatform, setSavingPlatform] = useState<string | null>(null);

  // Build the public profile URL from the current user's username
  const publicLink = user?.username ? `${window.location.origin}/profile/${user.username}` : '';

  // Hydrate local state from fetched data
  useEffect(() => {
    if (!linkData) return;
    setIsActive(linkData.shareable_link_active ?? false);
    setVisibleLinks(linkData.visible_social_links ?? {});
  }, [linkData]);

  // Platforms the user has actually configured; fall back to all platforms
  const configuredPlatforms = PLATFORMS.filter((p) => !!(user as any)?.[p.userKey]);
  const platformsToShow = configuredPlatforms.length > 0 ? configuredPlatforms : PLATFORMS;

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleToggleMain = async (checked: boolean) => {
    setTogglingMain(true);
    try {
      await updateMut.mutateAsync({ shareable_link_active: checked });
      setIsActive(checked);
    } finally {
      setTogglingMain(false);
    }
  };

  const handleTogglePlatform = async (apiKey: string, value: boolean) => {
    const updated = { ...visibleLinks, [apiKey]: value };
    setSavingPlatform(apiKey);
    try {
      await updateMut.mutateAsync({ visible_social_links: updated });
      setVisibleLinks(updated);
    } finally {
      setSavingPlatform(null);
    }
  };

  const handleCopy = useCallback(async () => {
    if (!publicLink) return;
    await navigator.clipboard.writeText(publicLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [publicLink]);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-black text-white pb-32">
      <Header />

      <main className="pt-24 md:pt-20 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 mb-8 text-zinc-500 text-[12px] font-medium">
          <Link
            to="/profile/settings"
            className="flex items-center gap-1.5 hover:text-white transition-colors"
          >
            <FiArrowLeft size={13} className="text-blue-500/80" />
            <span>Settings</span>
          </Link>
          <span className="text-zinc-700">/</span>
          <span className="text-zinc-200">Shareable Link</span>
        </nav>

        {/* Page header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-2xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center shrink-0">
            <FiLink size={20} className="text-cyan-400" />
          </div>
          <div>
            <h1 className="text-white font-bold text-[22px] leading-tight">Shareable Link</h1>
            <p className="text-zinc-500 text-[13px]">Share your socials and website in one link</p>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-60">
            <Spinner size={36} />
          </div>
        ) : (
          <div className="space-y-4">
            {/* ── Main toggle ── */}
            <div className="bg-zinc-900/30 border border-white/5 rounded-2xl p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <p className="text-white font-semibold text-[15px] mb-1">
                    Activate Shareable Link
                  </p>
                  <p className="text-zinc-500 text-[13px] leading-relaxed">
                    One link to share all your socials in one place. When active, anyone can visit
                    your public profile page.
                  </p>
                </div>
                <div className="shrink-0 mt-0.5">
                  <Toggle checked={isActive} onChange={handleToggleMain} loading={togglingMain} />
                </div>
              </div>
            </div>

            {/* ── Link display + social toggles (only when active) ── */}
            {isActive && (
              <>
                {/* Public link card */}
                <div className="bg-zinc-900/30 border border-white/5 rounded-2xl p-5">
                  <p className="text-zinc-400 text-[11px] font-semibold uppercase tracking-wider mb-3">
                    Your Public Link
                  </p>
                  <div className="flex items-center gap-2 bg-white/5 border border-white/8 rounded-xl px-4 py-3">
                    <FiLink size={14} className="text-cyan-400 shrink-0" />
                    <span className="flex-1 text-[13px] text-zinc-200 truncate font-mono min-w-0">
                      {publicLink || '—'}
                    </span>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={handleCopy}
                        disabled={!publicLink}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold transition-all border ${
                          copied
                            ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
                            : 'bg-white/8 text-zinc-300 hover:bg-white/12 border-white/10'
                        }`}
                      >
                        {copied ? <FiCheck size={11} /> : <FiCopy size={11} />}
                        {copied ? 'Copied!' : 'Copy'}
                      </button>
                      {publicLink && (
                        <a
                          href={publicLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 rounded-lg bg-white/8 text-zinc-400 hover:text-white hover:bg-white/12 border border-white/10 transition-all"
                          title="Open in new tab"
                        >
                          <FiExternalLink size={12} />
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                {/* Per-platform visibility */}
                <div className="bg-zinc-900/30 border border-white/5 rounded-2xl overflow-hidden">
                  <div className="px-5 py-4 border-b border-white/5">
                    <div className="flex items-center gap-2">
                      <p className="text-white font-semibold text-[14px]">What to Show</p>
                      <OptionalBadge />
                    </div>
                    <p className="text-zinc-500 text-[12px] mt-0.5">
                      Control which social links appear on your public profile
                    </p>
                  </div>

                  <div className="divide-y divide-white/5">
                    {platformsToShow.map(({ apiKey, label, emoji }) => {
                      const isVisible = visibleLinks[apiKey] ?? false;
                      const isSaving = savingPlatform === apiKey;

                      return (
                        <div key={apiKey} className="flex items-center justify-between px-5 py-3.5">
                          <div className="flex items-center gap-3">
                            <span className="text-[18px] leading-none w-6 text-center shrink-0">
                              {emoji}
                            </span>
                            <div>
                              <p className="text-white text-[13px] font-medium">{label}</p>
                              {isVisible ? (
                                <p className="text-emerald-500/70 text-[11px] flex items-center gap-1 mt-0.5">
                                  <FiEye size={9} /> Visible
                                </p>
                              ) : (
                                <p className="text-zinc-600 text-[11px] flex items-center gap-1 mt-0.5">
                                  <FiEyeOff size={9} /> Hidden
                                </p>
                              )}
                            </div>
                          </div>
                          <Toggle
                            size="sm"
                            checked={isVisible}
                            onChange={(v) => handleTogglePlatform(apiKey, v)}
                            loading={isSaving}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Tip card */}
                <div className="bg-cyan-500/5 border border-cyan-500/15 rounded-2xl p-4">
                  <p className="text-cyan-400/80 text-[12px] leading-relaxed">
                    💡 <strong>Tip:</strong> Add social links and your website in{' '}
                    <Link
                      to="/profile/edit"
                      className="underline underline-offset-2 hover:text-cyan-300 transition-colors"
                    >
                      Edit Profile
                    </Link>{' '}
                    to unlock more visibility options here.
                  </p>
                </div>
              </>
            )}
          </div>
        )}
      </main>

      <BottomNav />
    </div>
  );
};
